import { Link, useParams, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories, hotMangas, latestMangas, allMangas } from '@/mocks/manga';

const combinedMangas = [...hotMangas, ...latestMangas];

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sortBy = searchParams.get('sort') || 'popular';

  const activeCategory = categories.find((c) => c.slug === slug);
  const isAll = slug === 'all';

  const filtered = isAll
    ? allMangas
    : allMangas.filter((m) => activeCategory && m.categories.includes(activeCategory.name));

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === 'rating') return b.rating - a.rating;
    if (sortBy === 'latest') return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    return b.views - a.views;
  });

  const handleSort = (sort: string) => {
    setSearchParams({ sort });
  };

  const sortOptions = [
    { value: 'popular', label: '最热排行' },
    { value: 'rating', label: '评分最高' },
    { value: 'latest', label: '最新更新' },
  ];

  const categoryName = isAll ? '全部漫画' : (activeCategory?.name || '分类列表');
  const categoryDesc = isAll ? `共收录${allMangas.length}部精品韩漫` : `${activeCategory?.description || ''} · ${sorted.length}部作品`;
  const canonicalPath = isAll ? '/category/all' : `/category/${slug}`;

  const breadcrumbJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://waiwaimanhua.com/' },
      { '@type': 'ListItem', position: 2, name: isAll ? '全部漫画' : (activeCategory?.name || '分类'), item: `https://waiwaimanhua.com${canonicalPath}` },
    ],
  };

  const collectionJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${categoryName} - 歪歪漫画网`,
    description: `${categoryDesc}，歪歪漫画网提供最新高清免费韩国漫画大全在线观看。`,
    url: `https://waiwaimanhua.com${canonicalPath}`,
    hasPart: sorted.slice(0, 20).map((manga) => ({
      '@type': 'Book',
      name: manga.title,
      author: { '@type': 'Person', name: manga.author },
      image: manga.coverImage,
      url: `https://waiwaimanhua.com/comic/${manga.id}`,
    })),
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
      <script type="application/ld+json">{JSON.stringify(collectionJsonLd)}</script>
      <Navbar />
      <main className="pt-20 bg-background-50 min-h-screen">
        <section className="px-4 md:px-6 py-8">
          <nav aria-label="Breadcrumb" className="mb-6">
            <ol className="flex items-center gap-2 text-sm text-foreground-500">
              <li>
                <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
              </li>
              <li><i className="ri-arrow-right-s-line text-xs"></i></li>
              <li>
                <span className="text-foreground-700">{isAll ? '全部漫画' : activeCategory?.name || '分类'}</span>
              </li>
            </ol>
          </nav>

          <div className="mb-6">
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-2">
              {categoryName}
            </h1>
            <p className="text-sm text-foreground-500">
              {categoryDesc}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-2">
            <Link
              to="/category/all"
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                isAll
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-200 text-foreground-600 hover:bg-background-300'
              }`}
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer ${
                  slug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-200 text-foreground-600 hover:bg-background-300'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-6">
            {sortOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => handleSort(opt.value)}
                className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer ${
                  sortBy === opt.value
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {sorted.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
              {sorted.map((manga) => (
                <Link
                  key={manga.id}
                  to={`/comic/${manga.id}`}
                  className="group block rounded-xl overflow-hidden bg-background-100 transition-transform duration-300 hover:-translate-y-1 cursor-pointer"
                  data-product-shop=""
                >
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img
                      src={manga.coverImage}
                      alt={manga.title}
                      title={`${manga.title} - 歪歪漫画网`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-2 left-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        manga.status === '连载中'
                          ? 'bg-accent-500/90 text-background-50'
                          : 'bg-foreground-800/80 text-background-50'
                      }`}>
                        {manga.status}
                      </span>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3 pt-8">
                      <div className="flex items-center gap-1">
                        <i className="ri-star-fill text-accent-500 text-xs"></i>
                        <span className="text-xs text-white font-semibold">{manga.rating}</span>
                        <span className="text-xs text-white/70 ml-1">· {manga.chapters}话</span>
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <h3 className="text-sm font-medium text-foreground-900 truncate group-hover:text-primary-500 transition-colors">
                      {manga.title}
                    </h3>
                    <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {manga.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-700">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-200 flex items-center justify-center">
                <i className="ri-book-open-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-500">该分类下暂无漫画</p>
              <Link to="/category/all" className="text-sm text-primary-500 hover:underline mt-2 inline-block cursor-pointer">
                查看全部漫画
              </Link>
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}